"use client";

/**
 * Inventory 2.5 — student cart state (Browse → Cart, per DATA_FLOW.md: "client state
 * only, no DB until submit"). Deliberately separate from src/contexts/CartContext.tsx
 * (the V1/V2 cart, barcode-keyed, still used by non-inventory-2.5 surfaces until
 * inventory-fulfillment's Wave 3 cutover) — mixing the two key schemes would violate
 * the "item.id is the only canonical key" rule (identity.ts) the moment a V1/V2 line
 * and a 2.5 line sat in the same array.
 *
 * A line is keyed by `kind` + `refId` (a family id for a 'family' line, an item id for
 * everything else) — adding the same checkout unit twice adjusts qty in place rather
 * than creating a duplicate line, mirroring the old cart's dedupe behavior.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

export type Inventory25CartLineKind = 'family' | 'unit' | 'set' | 'supply';

export interface Inventory25CartLine {
  /** `${kind}:${refId}` — stable, used as the React key and the dedupe key. */
  id: string;
  kind: Inventory25CartLineKind;
  /** family id for 'family' lines, item id for unit/set/supply lines. */
  refId: string;
  displayName: string;
  qty: number;
  /** Cosmetic grouping (2026-08-07 experiment) — lines added together from one Folder
   * "Add" share a `groupId` so Cart can render them as a single card, without inventing
   * a new cart-line `kind` (submission's resolver only knows family/unit/set/supply and
   * must keep working unmodified). Undefined on every line added outside a Folder.
   * Also now read by inventory25requests.ts's `resolveLinesToItemRows` (fixed
   * 2026-08-09 — was dropped at submission time, `folder:`-prefixed onto the written
   * `request_items.group_id` so the student Requests view can box these members the
   * same way it boxes a Set. */
  groupId?: string;
  /** Folder display name shown as the group card's title. Only set alongside `groupId`. */
  groupLabel?: string;
}

interface Inventory25CartContextValue {
  lines: Inventory25CartLine[];
  addFamilyLine: (familyId: string, displayName: string, qty: number) => void;
  addLine: (
    kind: 'unit' | 'set' | 'supply',
    itemId: string,
    displayName: string,
    qty: number,
    group?: { groupId: string; groupLabel: string }
  ) => void;
  updateQty: (lineId: string, qty: number) => void;
  removeLine: (lineId: string) => void;
  clearCart: () => void;
  /** Student-adjusted override for one Family's shared-pool accessory line (Wireframes
   * doc §E: "Counts adjust with walkies — you can lower accessories if needed"). Keyed
   * `${familyId}:${accessoryItemId}`. Absent = use the auto-computed ceil(qty/ratio). */
  accessoryOverrides: Record<string, number>;
  setAccessoryOverride: (familyId: string, accessoryItemId: string, qty: number) => void;
  clearAccessoryOverride: (familyId: string, accessoryItemId: string) => void;
  /** Named members excluded from a 'set' line (e.g. skip the .6 filter from an ND
   * pouch) — per explicit user ask (2026-08-05 grilling session), mirrors the old
   * V1/V2 Group `excludedMembers` mechanism. Keyed by cart line id. Wired into
   * submission as of 2026-08-08 — `inventory25requests.ts`'s `resolveLinesToItemRows`
   * folds the excluded members' names into that Set's own `item_name` at submit time
   * (a Set is still one request_items row for the whole kit, not exploded per-member,
   * so this is how staff actually see the exclusion rather than a separate row being
   * dropped). `accessoryOverrides` below is a separate, still-unwired gap — do not
   * assume this comment covers it too. */
  excludedMembersByLineId: Record<string, string[]>;
  toggleExcludedMember: (lineId: string, memberId: string) => void;
}

const STORAGE_KEY = 'inventory25-cart';
const ACCESSORY_OVERRIDES_KEY = 'inventory25-cart-accessory-overrides';
const EXCLUDED_MEMBERS_KEY = 'inventory25-cart-excluded-members';

function readExcludedMembers(key: string): Record<string, string[]> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
  } catch {
    return {};
  }
}

function accessoryOverrideKey(familyId: string, accessoryItemId: string): string {
  return `${familyId}:${accessoryItemId}`;
}

function readAccessoryOverrides(key: string): Record<string, number> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

const Inventory25CartContext = createContext<Inventory25CartContextValue | null>(null);

function readStorage(key: string): Inventory25CartLine[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Inventory25CartLine[]) : [];
  } catch {
    return [];
  }
}

function lineId(kind: Inventory25CartLineKind, refId: string): string {
  return `${kind}:${refId}`;
}

/**
 * `storageNamespace` (added by fulfillment-staff, 2026-08-17) — optional prefix on the
 * three localStorage keys below. Defaults to `''`, which reproduces the exact prior
 * keys (`inventory25-cart`, etc.) byte-for-byte, so every existing student consumer is
 * unaffected. Lets a second, independently-scoped provider instance (e.g. wrapping
 * `/staff/browse` + `/staff/cart`) share this same Context/hook/BrowseGrid wiring
 * without staff and student cart lines colliding in localStorage on a shared browser
 * (front-desk kiosk risk) — see handoff-state.md's 2026-08-17 entry for the full
 * reasoning. inventory-student-experience: please review, this file is otherwise
 * unowned by fulfillment-staff.
 */
export function Inventory25CartProvider({
  children,
  storageNamespace = '',
}: {
  children: React.ReactNode;
  storageNamespace?: string;
}) {
  const storageKey = storageNamespace ? `${storageNamespace}-${STORAGE_KEY}` : STORAGE_KEY;
  const accessoryKey = storageNamespace ? `${storageNamespace}-${ACCESSORY_OVERRIDES_KEY}` : ACCESSORY_OVERRIDES_KEY;
  const excludedKey = storageNamespace ? `${storageNamespace}-${EXCLUDED_MEMBERS_KEY}` : EXCLUDED_MEMBERS_KEY;

  const [lines, setLines] = useState<Inventory25CartLine[]>(() => readStorage(storageKey));
  const [accessoryOverrides, setAccessoryOverrides] = useState<Record<string, number>>(() => readAccessoryOverrides(accessoryKey));
  const [excludedMembersByLineId, setExcludedMembersByLineId] = useState<Record<string, string[]>>(() => readExcludedMembers(excludedKey));

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(lines));
  }, [lines, storageKey]);

  useEffect(() => {
    window.localStorage.setItem(accessoryKey, JSON.stringify(accessoryOverrides));
  }, [accessoryOverrides, accessoryKey]);

  useEffect(() => {
    window.localStorage.setItem(excludedKey, JSON.stringify(excludedMembersByLineId));
  }, [excludedMembersByLineId, excludedKey]);

  const toggleExcludedMember = useCallback((targetLineId: string, memberId: string) => {
    setExcludedMembersByLineId((prev) => {
      const current = prev[targetLineId] ?? [];
      const next = current.includes(memberId) ? current.filter((id) => id !== memberId) : [...current, memberId];
      return { ...prev, [targetLineId]: next };
    });
  }, []);

  const setAccessoryOverride = useCallback((familyId: string, accessoryItemId: string, qty: number) => {
    setAccessoryOverrides((prev) => ({ ...prev, [accessoryOverrideKey(familyId, accessoryItemId)]: Math.max(0, qty) }));
  }, []);

  const clearAccessoryOverride = useCallback((familyId: string, accessoryItemId: string) => {
    setAccessoryOverrides((prev) => {
      const next = { ...prev };
      delete next[accessoryOverrideKey(familyId, accessoryItemId)];
      return next;
    });
  }, []);

  const upsertLine = useCallback(
    (
      kind: Inventory25CartLineKind,
      refId: string,
      displayName: string,
      qty: number,
      group?: { groupId: string; groupLabel: string }
    ) => {
      if (qty <= 0) return;
      setLines((prev) => {
        const id = lineId(kind, refId);
        const existing = prev.find((l) => l.id === id);
        // Re-adding a line that's already sitting in the cart (e.g. it was added solo,
        // then bundled into a Folder add) adopts the new group rather than keeping the
        // old one — matches "one Add = one group" intent instead of leaving orphaned
        // group metadata behind.
        if (existing) {
          return prev.map((l) =>
            l.id === id ? { ...l, qty, groupId: group?.groupId, groupLabel: group?.groupLabel } : l
          );
        }
        return [...prev, { id, kind, refId, displayName, qty, groupId: group?.groupId, groupLabel: group?.groupLabel }];
      });
    },
    []
  );

  const addFamilyLine = useCallback(
    (familyId: string, displayName: string, qty: number) => upsertLine('family', familyId, displayName, qty),
    [upsertLine]
  );

  const addLine = useCallback(
    (
      kind: 'unit' | 'set' | 'supply',
      itemId: string,
      displayName: string,
      qty: number,
      group?: { groupId: string; groupLabel: string }
    ) => upsertLine(kind, itemId, displayName, qty, group),
    [upsertLine]
  );

  const updateQty = useCallback((targetLineId: string, qty: number) => {
    setLines((prev) =>
      qty <= 0
        ? prev.filter((l) => l.id !== targetLineId)
        : prev.map((l) => (l.id === targetLineId ? { ...l, qty } : l))
    );
  }, []);

  const removeLine = useCallback((targetLineId: string) => {
    setLines((prev) => prev.filter((l) => l.id !== targetLineId));
    setExcludedMembersByLineId((prev) => {
      if (!(targetLineId in prev)) return prev;
      const next = { ...prev };
      delete next[targetLineId];
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setLines([]);
    setAccessoryOverrides({});
    setExcludedMembersByLineId({});
  }, []);

  const value = useMemo(
    () => ({
      lines,
      addFamilyLine,
      addLine,
      updateQty,
      removeLine,
      clearCart,
      accessoryOverrides,
      setAccessoryOverride,
      clearAccessoryOverride,
      excludedMembersByLineId,
      toggleExcludedMember,
    }),
    [
      lines,
      addFamilyLine,
      addLine,
      updateQty,
      removeLine,
      clearCart,
      accessoryOverrides,
      setAccessoryOverride,
      clearAccessoryOverride,
      excludedMembersByLineId,
      toggleExcludedMember,
    ]
  );

  return <Inventory25CartContext.Provider value={value}>{children}</Inventory25CartContext.Provider>;
}

export function useInventory25Cart() {
  const ctx = useContext(Inventory25CartContext);
  if (!ctx) throw new Error('useInventory25Cart must be used within Inventory25CartProvider');
  return ctx;
}
